import React, { FC } from 'react'
import { Placement } from '@popperjs/core'

import { StyledDropdown, Label, DropdownContent } from './Select.styles.old'
import SelectButton from './SelectButton'
import NativeSelect from './NativeSelect.styles'
import Breakpoint from 'components/Breakpoint'

type HTMLSelectProps = React.SelectHTMLAttributes<HTMLSelectElement>

interface SelectProps extends HTMLSelectProps {
  children: React.ReactNode
  onToggle?: (visible: boolean) => void
  placement?: Placement
  disabled?: boolean
  defaultLabel?: string
  label?: React.ReactNode
  invalid?: boolean
  fillContent?: boolean
  /**
   * Provide the list of options for the mobile version of the select (Native select).
   * The <Select> component switches to NativeSelect under the hood on small viewports.
   * Normally Select has a hacky mechanism to build this optionsForNativeSelect list from
   * the children but this does not work for more complex structure (fragments, options with children
   * that are ReactNodes instead of strings...). This prop allows you to overwrite this hacky parsing
   * by providing manually the list of possible options.
   */
  optionsForNativeSelect?: NativeOptionConfig[]
}

/** Reads the children of this Select component and returns the strings for
 * the <option> tags as well as their onClick action function
 */
export interface NativeOptionConfig {
  value?: string
  name?: string
  action?: () => void
}
const getOptions = (children: React.ReactNode): NativeOptionConfig[] => {
  const options: NativeOptionConfig[] = []
  React.Children.forEach(children, child => {
    if (
      child &&
      typeof child === 'object' &&
      'props' in child &&
      typeof child?.props?.children === 'string'
    ) {
      options.push({
        value: child.props.value,
        name: child.props.children,
        action: child.props.onClick,
      })
    }
  })

  return options
}

/**
 * Returns a function that gets the currently select `<option>` value
 * This function finds the corresponding `onClick` action function and calls it
 */
const selectNativeOption = (options: NativeOptionConfig[]) => (
  e: React.ChangeEvent<HTMLSelectElement>
) => {
  const value = e?.target.value
  const selectedOption = options.find(opt => opt.value === value)
  selectedOption?.action?.()
}

/**
 * @deprecated use `import { Select } from 'components/Select'`
 *
 * @link https://github.com/sketch-hq/cloud-frontend/pull/3146
 */
const Select: FC<SelectProps> = ({
  placement = 'bottom-start',
  disabled,
  invalid,
  label,
  defaultLabel,
  optionsForNativeSelect,
  ...props
}) => (
  <Breakpoint on="sm">
    {notSmall => {
      if (notSmall || disabled || invalid) {
        return (
          <StyledDropdown
            placement={placement}
            spacing="4px"
            maxWidth="100%"
            disabled={disabled}
            fillContent
            {...props}
            toggle={
              <SelectButton disabled={disabled} invalid={invalid}>
                <Label>{label || defaultLabel}</Label>
              </SelectButton>
            }>
            <DropdownContent>{props.children}</DropdownContent>
          </StyledDropdown>
        )
      } else {
        // Mobile version - uses native html elements so that the mobile OS
        // displays their native selectors
        const options = optionsForNativeSelect || getOptions(props.children)
        const isAnOptionSelected = options.some(
          opt => opt.value === props.defaultValue
        )
        return (
          <NativeSelect onChange={selectNativeOption(options)}>
            {!optionsForNativeSelect && (!isAnOptionSelected || label) && (
              // Show the label instead of the selected option
              <option value="" disabled={true} selected={true}>
                {label || defaultLabel}
              </option>
            )}
            {options.map(({ value, name }) => (
              <option
                key={value}
                value={value}
                selected={props.defaultValue === value}>
                {name}
              </option>
            ))}
          </NativeSelect>
        )
      }
    }}
  </Breakpoint>
)

export default Select
